import React, { useState } from 'react';
import TrendChart from './TrendChart';
import '../styles/HealthSummaryCard.css';

interface HealthSummaryCardProps {
  onViewBodyOverview?: () => void;
  onRiskInfoOpen?: (riskType: 'ascvd' | 'tumor') => void;
}

// 动态血管风险演示组件
const DynamicVesselRiskDemo: React.FC<{ onDetailsClick: () => void }> = ({ onDetailsClick }) => {
  const [showDamageDetails, setShowDamageDetails] = useState(false);

  return (
    <div className="vessel-risk-demo">
      <div className="demo-header">
        <div className="risk-title-with-pulse">
          <span className="demo-title">ASCVD风险</span>
          <div className="risk-pulse-dot"></div>
        </div>
        <div className="risk-percentage-large">
          <span className="percentage-number">12%</span>
          <span className="percentage-label">10年风险</span>
        </div>
      </div>

      {/* 风险演示标题 */}
      <div className="demo-section-title compact">
        <span className="section-title-text">风险演示</span>
      </div>

      {/* 动态血管演示 */}
      <div className="vessel-progression-container">
        <div className="vessel-stage" data-stage="healthy">
          <div className="vessel-demo healthy">
            <div className="vessel-wall-demo">
              <div className="vessel-inner healthy">
                <div className="blood-flow-demo healthy">
                  <div className="blood-particle p1"></div>
                  <div className="blood-particle p2"></div>
                  <div className="blood-particle p3"></div>
                  <div className="blood-particle p4"></div>
                  <div className="blood-particle p5"></div>
                </div>
              </div>
            </div>
            <div className="stage-info">
              <span className="stage-title">现在</span>
              <span className="stage-risk">5%</span>
            </div>
          </div>
        </div>

        <div className="progression-arrow">→</div>

        <div className="vessel-stage" data-stage="progressing">
          <div className="vessel-demo progressing">
            <div className="vessel-wall-demo">
              <div className="plaque-formation small"></div>
              <div className="vessel-inner progressing">
                <div className="blood-flow-demo progressing">
                  <div className="blood-particle p1"></div>
                  <div className="blood-particle p2"></div>
                  <div className="blood-particle p3"></div>
                  <div className="blood-particle p4"></div>
                </div>
              </div>
            </div>
            <div className="stage-info">
              <span className="stage-title">5年后</span>
              <span className="stage-risk">10%</span>
            </div>
          </div>
        </div>

        <div className="progression-arrow">→</div>

        <div className="vessel-stage" data-stage="critical">
          <div className="vessel-demo critical">
            <div className="vessel-wall-demo">
              <div className="plaque-formation large"></div>
              <div className="blood-clot-formation"></div>
              <div className="vessel-inner critical">
                <div className="blood-flow-demo critical">
                  <div className="blood-particle p1"></div>
                  <div className="blood-particle p2"></div>
                  <div className="blood-particle p3"></div>
                  <div className="blood-particle stuck"></div>
                </div>
                <div className="blockage-indicator"></div>
              </div>
            </div>
            <div className="stage-info">
              <span className="stage-title">10年后</span>
              <span className="stage-risk">12%</span>
            </div>
          </div>
        </div>
      </div>

      {/* 器官损伤展开入口 - 放在10年血管下面 */}
      <div className="damage-trigger-section">
        <button 
          className="damage-details-trigger below-vessel"
          onClick={() => setShowDamageDetails(!showDamageDetails)}
        >
          <span className="trigger-icon">⚠️</span>
          <span className="trigger-text">
            {showDamageDetails ? '隐藏器官损伤详情' : '查看器官损伤详情'}
          </span>
          <span className={`trigger-arrow ${showDamageDetails ? 'expanded' : ''}`}>
            ▼
          </span>
        </button>
      </div>

      {/* 器官损伤详情 - 可折叠 */}
      {showDamageDetails && (
        <div className="consequences-showcase animated-expand">
          <div className="consequence-header">
            <span className="warning-icon">⚠️</span>
            <span className="consequence-title">血流受阻导致的器官损伤</span>
          </div>
          
          <div className="consequences-grid">
            <div className="consequence-item heart-attack">
              <div className="consequence-icon">
                <div className="organ-damage-demo heart">
                  <div className="organ-outline heart-outline">
                    <div className="organ-shape">♥</div>
                    <div className="damage-area heart-damage">
                      <div className="damage-spread"></div>
                      <div className="damage-core"></div>
                    </div>
                    <div className="blocked-artery">
                      <div className="artery-line"></div>
                      <div className="blockage-point"></div>
                    </div>
                    <div className="pain-waves"></div>
                  </div>
                </div>
              </div>
              <div className="consequence-info">
                <span className="consequence-name">心肌梗死</span>
                <span className="consequence-desc">心肌缺血坏死</span>
                <div className="damage-severity severe">严重损伤</div>
              </div>
            </div>

            <div className="consequence-item stroke">
              <div className="consequence-icon">
                <div className="organ-damage-demo brain">
                  <div className="organ-outline brain-outline">
                    <div className="organ-shape">🧠</div>
                    <div className="damage-area brain-damage">
                      <div className="damage-spread"></div>
                      <div className="damage-core"></div>
                    </div>
                    <div className="blocked-vessel">
                      <div className="vessel-line"></div>
                      <div className="blockage-point"></div>
                    </div>
                    <div className="dysfunction-indicator">
                      <div className="signal-disruption"></div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="consequence-info">
                <span className="consequence-name">缺血性中风</span>
                <span className="consequence-desc">脑组织缺血</span>
                <div className="damage-severity critical">功能障碍</div>
              </div>
            </div>

            <div className="consequence-item peripheral">
              <div className="consequence-icon">
                <div className="organ-damage-demo peripheral">
                  <div className="organ-outline peripheral-outline">
                    <div className="limb-shape">🦵</div>
                    <div className="damage-area peripheral-damage">
                      <div className="damage-spread"></div>
                      <div className="damage-core"></div>
                    </div>
                    <div className="blocked-circulation">
                      <div className="circulation-line"></div>
                      <div className="blockage-point"></div>
                    </div>
                    <div className="ischemia-indicator"></div>
                  </div>
                </div>
              </div>
              <div className="consequence-info">
                <span className="consequence-name">肢体缺血</span>
                <span className="consequence-desc">外周血管病变</span>
                <div className="damage-severity moderate">循环障碍</div>
              </div>
            </div>
          </div>

          {/* 损伤进程动画 */}
          <div className="damage-progression">
            <div className="progression-title">器官损伤发展过程</div>
            <div className="progression-stages">
              <div className="damage-stage" data-stage="blockage">
                <div className="stage-visual">
                  <div className="vessel-blocked">
                    <div className="vessel-tube"></div>
                    <div className="clot-obstruction"></div>
                  </div>
                </div>
                <div className="stage-label">血管堵塞</div>
              </div>
              
              <div className="damage-arrow">→</div>
              
              <div className="damage-stage" data-stage="ischemia">
                <div className="stage-visual">
                  <div className="organ-ischemia">
                    <div className="organ-cell normal"></div>
                    <div className="organ-cell dying"></div>
                    <div className="organ-cell dead"></div>
                  </div>
                </div>
                <div className="stage-label">缺血缺氧</div>
              </div>
              
              <div className="damage-arrow">→</div>
              
              <div className="damage-stage" data-stage="necrosis">
                <div className="stage-visual">
                  <div className="tissue-necrosis">
                    <div className="necrotic-area"></div>
                    <div className="inflammation"></div>
                  </div>
                </div>
                <div className="stage-label">组织坏死</div>
              </div>
              
              <div className="damage-arrow">→</div>
              
              <div className="damage-stage" data-stage="dysfunction">
                <div className="stage-visual">
                  <div className="organ-dysfunction">
                    <div className="function-loss"></div>
                    <div className="compensation-attempt"></div>
                  </div>
                </div>
                <div className="stage-label">功能丧失</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 风险因素指示器 */}
      <div className="risk-factors-section">
        <h4 className="factors-title">风险因素</h4>
        <div className="risk-factors-visual">
          <div className="factor-indicator-visual active">
            <div className="factor-dot warning"></div>
            <span>血脂↗</span>
          </div>
          <div className="factor-indicator-visual">
            <div className="factor-dot normal"></div>
            <span>血压→</span>
          </div>
          <div className="factor-indicator-visual">
            <div className="factor-dot normal"></div>
            <span>血糖→</span>
          </div>
        </div>
      </div>

      {/* 干预效果预览 */}
      <div className="intervention-effect">
        <div className="effect-header">
          <span className="effect-icon">✨</span>
          <span>生活方式干预效果</span>
        </div>
        <div className="effect-comparison">
          <div className="effect-before">
            <span className="effect-percentage">12%</span>
            <span className="effect-label">当前风险</span>
          </div>
          <div className="effect-arrow">
            <div className="arrow-line"></div>
            <div className="arrow-head"></div>
          </div>
          <div className="effect-after">
            <span className="effect-percentage success">≤6%</span>
            <span className="effect-label">干预后</span>
          </div>
        </div>
      </div>

      {/* 查看详情按钮 */}
      <button className="demo-detail-btn" onClick={onDetailsClick}>
        <span className="btn-text">深入了解风险机制与逆转方案</span>
        <div className="btn-glow"></div>
      </button>
    </div>
  );
};

const HealthSummaryCardC: React.FC<HealthSummaryCardProps> = ({ onViewBodyOverview, onRiskInfoOpen }) => {
  
  const bloodLipidData = [
    { date: '2024-01', value: 5.2 },
    { date: '2024-02', value: 5.8 },
    { date: '2024-03', value: 6.1 },
    { date: '2024-04', value: 5.9 },
    { date: '2024-05', value: 5.6 },
    { date: '2024-06', value: 5.4 }
  ];

  const lungNoduleData = [
    { date: '2024-01', value: 3.2 },
    { date: '2024-02', value: 3.1 },
    { date: '2024-03', value: 3.3 },
    { date: '2024-04', value: 3.2 },
    { date: '2024-05', value: 3.0 },
    { date: '2024-06', value: 2.9 }
  ];

  const handleViewAllTrends = () => {
    console.log('查看全部指标趋势');
  };

  const handleAppointment = (type: string) => {
    console.log(`预约${type}检查`);
  };

  const openRiskInfoPage = (riskType: 'ascvd' | 'tumor') => {
    if (onRiskInfoOpen) {
      onRiskInfoOpen(riskType);
    }
  };

  return (
    <div className="health-summary-card">
      {/* 卡片头部 */}
      <div className="summary-header">
        <h4>健康概况</h4>
        <span className="update-time-right">2024-06-15</span>
      </div>

      {/* 健康摘要 + 风险评估整合版 */}
      <div className="summary-section">
        <div className="unified-health-overview">
          {/* 摘要文字 */}
          <div className="overview-summary">
            <p>整体健康状况良好，需重点关注血脂水平偏高和肺部小结节变化。建议定期复查，保持健康的生活方式。</p>
          </div>
          
          {/* 风险评估卡片 - C版本全新动画设计 */}
          <div className="risk-cards-container">
            {/* 只展示有风险的项目 - 动态血管风险演示 */}
            <DynamicVesselRiskDemo onDetailsClick={() => openRiskInfoPage('ascvd')} />
          </div>
        </div>
      </div>
      
      {/* 2. 重点指标趋势 */}
      <div className="trends-section">
        <h4>重点指标趋势</h4>
        <div className="charts-container">
          <div className="chart-item">
            <div className="chart-header">
              <h5>血脂水平 (mmol/L)</h5>
              <div className="chart-target-info">
                <span className="target-label">目标值: &lt; 5.2</span>
              </div>
            </div>
            <TrendChart 
              data={bloodLipidData} 
              color="#FF6B6B" 
              unit=" mmol/L"
              thresholdValue={5.2}
              thresholdLabel="正常上限"
            />
          </div>
          <div className="chart-item">
            <div className="chart-header">
              <h5>肺结节大小 (mm)</h5>
              <div className="chart-target-info">
                <span className="target-label">监测值: &lt; 5.0</span>
              </div>
            </div>
            <TrendChart 
              data={lungNoduleData} 
              color="#4ECDC4" 
              unit=" mm"
              thresholdValue={5.0}
              thresholdLabel="关注线"
            />
          </div>
        </div>
        <div className="trends-actions">
          <button className="view-all-trends-external" onClick={handleViewAllTrends}>
            查看全部指标 <span className="arrow-icon">→</span>
          </button>
        </div>
      </div>

      {/* 3. 推荐处理方式 - 重新优化 */}
      <div className="recommendations-section">
        <h4>下一步最佳行动</h4>
        <div className="recommendation-items-clean">
          <div className="recommendation-item-enhanced">
            <div className="rec-content-enhanced">
              <div className="rec-main-content">
                <h5>血脂管理</h5>
                <p>控制血脂水平，降低心血管风险</p>
              </div>
              <span className="rec-reminder">添加提醒</span>
            </div>
          </div>
          
          <div className="recommendation-item-enhanced">
            <div className="rec-content-enhanced">
              <div className="rec-main-content">
                <h5>肺结节监测</h5>
                <p>定期CT检查，观察结节变化</p>
              </div>
              <span className="rec-reminder">添加提醒</span>
            </div>
          </div>
          
          <div className="recommendation-item-enhanced">
            <div className="rec-content-enhanced">
              <div className="rec-main-content">
                <h5>营养咨询</h5>
                <p>专业营养师指导，制定个性化饮食方案</p>
              </div>
              <button 
                className="call-doctor-btn"
                onClick={() => handleAppointment('营养咨询')}
              >
                呼叫医生
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 5. 引用资料 */}
      <div className="references-section">
        <h4>引用资料</h4>
        <div className="reference-items">
          <div className="reference-item"><span>中国成人血脂异常防治指南(2023版)</span></div>
          <div className="reference-item"><span>肺结节诊治中国专家共识(2022版)</span></div>
        </div>
      </div>
    </div>
  );
};

export default HealthSummaryCardC; 